import { useEffect, useMemo, useState } from 'react';
import 'react-tabs/style/react-tabs.scss';
import { Tabs, TabList, Tab, TabPanel } from 'react-tabs';
import './AutoFilterViewSettings.scss';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import {
  getAutoFilterSettings,
  resetSettings,
  selectAutoFilterLevelSettings,
  selectEnabledSettingsFilterViewMetadata,
  setAutoFilterLevelFieldSettings,
  setMinLOIsForAutoFiltering,
  updateAutoFilterSettings,
} from '../../../../redux/reducers/settingsSlice';
import classNames from 'classnames';
import { selectFields } from '../../../../redux/reducers/metadataSlice';
import levelFlow from './../../../../assets/images/dialog/settings/Level_Black_Dots_Line.svg';
import {
  AutoFilterLevelSettingsMeta,
  AutoFilterSettings,
  FieldEntity,
} from '../../../../common/types/EntityTypes';
import removeCross from './../../../../assets/images/dialog/settings/removeCross.svg';
import arrowUp from './../../../../assets/images/dialog/settings/arrowUp.svg';
import arrowDown from './../../../../assets/images/dialog/settings/arrowDown.svg';
import arrowDownGray from './../../../../assets/images/dialog/settings/arrowDownGray.svg';
import { setSettings } from '../../../../redux/reducers/popupSlice';

export default function AutoFilterViewSettings() {
  const dispatch = useAppDispatch();
  const filterViewData = useAppSelector(selectEnabledSettingsFilterViewMetadata);
  const fields = useAppSelector(selectFields);
  const dbAutoFilterSettings = useAppSelector(selectAutoFilterLevelSettings);

  // const [tabIndex, setTabIndex] = useState(0);
  // const activeFilterId = filterViewData[tabIndex]?.externalId;
  const [activeFilterId, setActiveFilterId] = useState<string>('');

  const [allFilterSettings, setAllFilterSettings] = useState<AutoFilterSettings[]>([]);
  const [currentLevelState, setCurrentLevelState] = useState<AutoFilterLevelSettingsMeta[]>([]);
  const [fieldAlreadyLevelled, setFieldAlreadyLevelled] = useState<string[]>([]);
  const [fieldToBeLevelled, setFieldToBeLevelled] = useState<FieldEntity[]>([]);
  const [minLOIMap, setMinLOIMap] = useState<Record<string, number>>({});
  const [isSettingsModified, setSettingsModified] = useState(false);

  const levels = Array.from({ length: 8 }, (_, i) => `Level ${i + 1}`);

  const fieldsToBeIncluded = [
    '001a',
    '002a',
    '003m',
    '003r',
    '003f',
    '003a',
    '003d',
    '001d',
    '001e',
    '031a',
    '032a',
  ];

  const availableFields = useMemo(
    () =>
      fields.filter(
        (f) => !fieldAlreadyLevelled.includes(f.code) && fieldsToBeIncluded.includes(f.code),
      ),
    [fields, fieldAlreadyLevelled, fieldsToBeIncluded],
  );

  // const minLOIsForAutoFilter = minLOIMap[activeFilterId ?? ''] ?? 2;

  // Fetch data on mount
  useEffect(() => {
    if (!filterViewData.length) return;
    const inboxFilter = filterViewData.find((f) => f.name?.toLowerCase() === 'inbox');
    if (inboxFilter) {
      setActiveFilterId(inboxFilter.externalId);

      dispatch(getAutoFilterSettings(activeFilterId)).then((resp) => {
        const receivedData: Record<string, AutoFilterLevelSettingsMeta[]> =
          resp.payload?.data ?? {};
        const dataForInbox = receivedData[activeFilterId] ?? [
          {
            filterText: '2',
            filterType: 'MINIMUM_LOI',
            uiOrder: 0,
            fieldName: '',
          },
        ];

        const transformed: AutoFilterSettings[] = [
          {
            filterViewexternalId: activeFilterId,
            values: dataForInbox,
          },
        ];

        dispatch(setAutoFilterLevelFieldSettings(transformed));
        setAllFilterSettings(transformed);

        // Initialize the minLOI map without overriding existing values
        const minItem = dataForInbox.find((v) => v.filterType === 'MINIMUM_LOI');
        setMinLOIMap({ [activeFilterId]: Number(minItem?.filterText) || 2 });
      });
    }
  }, [dispatch, filterViewData, activeFilterId]);

  // When tab changes, update level and fields from DB
  useEffect(() => {
    if (!activeFilterId || allFilterSettings.length === 0) return;

    const activeSettings = allFilterSettings.find((e) => e.filterViewexternalId === activeFilterId);

    if (activeSettings) {
      const fieldsMap = new Map(fields.map((item) => [item.code, item.name]));
      const selected = activeSettings.values
        .filter((v) => v.filterType !== 'MINIMUM_LOI')
        .map((v) => ({
          ...v,
          fieldName: fieldsMap.get(v.filterText) ?? '',
        }));

      setCurrentLevelState(selected);
      setFieldAlreadyLevelled(selected.map((e) => e.filterText));

      // Keep existing value or pick from DB if not set
      const minLoiItem = activeSettings.values.find((v) => v.filterType === 'MINIMUM_LOI');
      setMinLOIMap((prev) => ({
        ...prev,
        [activeFilterId]: prev[activeFilterId] ?? (Number(minLoiItem?.filterText) || 2),
      }));
    }
  }, [activeFilterId, allFilterSettings, fields]);

  function transformAutoFilterSettings(
    payload: Record<string, AutoFilterLevelSettingsMeta[]>,
  ): AutoFilterSettings[] {
    return filterViewData.map((filter) => {
      const existingValues = payload[filter.externalId] || [];
      return {
        filterViewexternalId: filter.externalId,
        values: existingValues.map((v) => ({
          uiOrder: v.uiOrder ?? 0,
          filterType: v.filterType ?? '',
          filterText: v.filterText ?? '',
          fieldName: v.fieldName ?? '',
        })),
      };
    });
  }

  function addNewLevel() {
    if (fieldToBeLevelled.length === 0) return;
    setSettingsModified(true);
    const nextOrder = currentLevelState.length + 2;
    const newLevel: AutoFilterLevelSettingsMeta = {
      filterText: fieldToBeLevelled[0].code,
      fieldName: fieldToBeLevelled[0].name,
      filterType: 'FIELD',
      uiOrder: nextOrder,
    };

    setFieldAlreadyLevelled((prev) => [...prev, newLevel.filterText]);
    setCurrentLevelState((prev) => [...prev, newLevel]);
    setFieldToBeLevelled([]);

    setAllFilterSettings((prev) =>
      prev.map((e) =>
        e.filterViewexternalId === activeFilterId ? { ...e, values: [...e.values, newLevel] } : e,
      ),
    );
  }

  function normalizeLevels(items: AutoFilterLevelSettingsMeta[]): AutoFilterLevelSettingsMeta[] {
    const sorted = [...items].sort((a, b) => Number(a.uiOrder) - Number(b.uiOrder));
    let order = 2;
    return sorted.map((item) =>
      item.filterType === 'MINIMUM_LOI' ? item : { ...item, uiOrder: order++ },
    );
  }

  const removeLevel = (_: string, fieldText: string) => {
    setSettingsModified(true);
    setFieldAlreadyLevelled((prev) => prev.filter((f) => f !== fieldText));

    const newState = normalizeLevels(
      currentLevelState.filter((item) => item.filterText !== fieldText),
    );
    setCurrentLevelState(newState);

    // Update only the active filter’s data
    setAllFilterSettings((prev) =>
      prev.map(
        (e) =>
          e.filterViewexternalId === activeFilterId
            ? { ...e, values: normalizeLevels(e.values.filter((v) => v.filterText !== fieldText)) }
            : e, // ← leave others unchanged
      ),
    );
  };

  const increase = () => {
    setSettingsModified(true);
    setMinLOIMap((prev) => {
      const newVal = (prev[activeFilterId ?? ''] ?? 2) + 1;
      dispatch(setMinLOIsForAutoFiltering(newVal));
      return { ...prev, [activeFilterId ?? '']: newVal };
    });
  };

  const decrease = () => {
    const current = minLOIMap[activeFilterId ?? ''] ?? 2;
    if (current <= 1) return;
    setSettingsModified(true);
    setMinLOIMap((prev) => {
      const newVal = current - 1;
      dispatch(setMinLOIsForAutoFiltering(newVal));
      return { ...prev, [activeFilterId ?? '']: newVal };
    });
  };

  const handleResetToDefault = () => {
    // dispatch(resetSettings(activeFilterId)).then((resp) => {
    //   if (resp.payload) {
        // dispatch(setAutoFilterLevelFieldSettings([]));
        setSettingsModified(true);
        setFieldAlreadyLevelled([]);
        setFieldToBeLevelled([]);
        setCurrentLevelState([]);
        setMinLOIMap((prev) => ({ ...prev, [activeFilterId ?? '']: 2 }));
      // }
    // });
  };

  const handleClose = () => dispatch(setSettings(false));

  const handleCancel = () => {
    setSettingsModified(false);

    // Find current filter's DB values
    const dbFilter = dbAutoFilterSettings.find((e) => e.filterViewexternalId === activeFilterId);

    if (!dbFilter) return;

    // Separate MINIMUM_LOI and LEVEL entries
    const dbMinLoi = dbFilter.values.find((v) => v.filterType === 'MINIMUM_LOI');
    const dbLevels = dbFilter.values.filter((v) => v.filterType !== 'MINIMUM_LOI');

    const fieldsMap = new Map(fields.map((f) => [f.code, f.name]));

    // Restore UI level state with field names
    const restoredLevels = dbLevels.map((v) => ({
      ...v,
      fieldName: fieldsMap.get(v.filterText) ?? '',
    }));

    // Update state back to DB snapshot
    setCurrentLevelState(restoredLevels);
    setFieldAlreadyLevelled(restoredLevels.map((v) => v.filterText));
    setFieldToBeLevelled([]);

    // Restore MINIMUM_LOI for this tab
    setMinLOIMap((prev) => ({
      ...prev,
      [activeFilterId ?? '']: Number(dbMinLoi?.filterText) || 2,
    }));

    // Also ensure global filter settings reflect DB snapshot for this tab
    setAllFilterSettings((prev) =>
      prev.map((f) =>
        f.filterViewexternalId === activeFilterId ? { ...f, values: dbFilter.values } : f,
      ),
    );
  };

  // function getPayloadWithChanges(
  //   existing: AutoFilterSettings[],
  //   activeFilterId: string,
  //   currentLevelState: AutoFilterLevelSettingsMeta[],
  // ): AutoFilterSettings[] {
  // const filterIds = filterViewData.map((f) => f.externalId);

  // return filterIds.map((fid) => {
  //   const existingItem = existing.find((i) => i.filterViewexternalId === fid);
  //   const minVal = minLOIMap[fid] ?? 2;

  // if (fid === activeFilterId) {
  // Update only current tab’s data
  // return {
  //   filterViewexternalId: fid,
  //   values: [
  //     {
  //       filterText: minVal.toString(),
  //       filterType: 'MINIMUM_LOI',
  //       uiOrder: 0,
  //       fieldName: '',
  //     },
  //     ...currentLevelState,
  //   ],
  // };
  // }

  //   // ✅ Keep previous state for other tabs (don’t override!)
  //   if (existingItem && existingItem.values.length > 0) {
  //     const hasMin = existingItem.values.some((v) => v.filterType === 'MINIMUM_LOI');
  //     const updatedValues = hasMin
  //       ? existingItem.values.map((v) =>
  //           v.filterType === 'MINIMUM_LOI' ? { ...v, filterText: minVal.toString() } : v,
  //         )
  //       : [
  //           {
  //             filterText: minVal.toString(),
  //             filterType: 'MINIMUM_LOI',
  //             uiOrder: 0,
  //             fieldName: '',
  //           },
  //           ...existingItem.values,
  //         ];

  //     return { ...existingItem, values: updatedValues };
  //   }

  //   // Add new entry for uninitialized filters
  //   return {
  //     filterViewexternalId: fid,
  //     values: [
  //       {
  //         filterText: minVal.toString(),
  //         filterType: 'MINIMUM_LOI',
  //         uiOrder: 0,
  //         fieldName: '',
  //       },
  //     ],
  //   };
  // });
  // }

  // const handleSyncLevelData = () => {
  //   if (!activeFilterId || currentLevelState.length === 0) return;

  //   const activeMinLOI = minLOIMap[activeFilterId] ?? 2;

  //   // Create synced configuration for all filters (local only)
  //   const syncedFilters = allFilterSettings.map((filter) => ({
  //     ...filter,
  //     values: [
  //       {
  //         filterText: activeMinLOI.toString(),
  //         filterType: 'MINIMUM_LOI',
  //         uiOrder: 0,
  //         fieldName: '',
  //       },
  //       ...currentLevelState.map((level) => ({
  //         ...level,
  //         uiOrder: level.uiOrder,
  //       })),
  //     ],
  //   }));

  //   // Update in-memory states
  //   setAllFilterSettings(syncedFilters);

  //   // Update all minLOI values to active one
  //   setMinLOIMap((prev) => {
  //     const updated: Record<string, number> = { ...prev };
  //     filterViewData.forEach((f) => {
  //       updated[f.externalId] = activeMinLOI;
  //     });
  //     return updated;
  //   });

  //   // Mark as modified so Apply becomes active
  //   setSettingsModified(true);
  // };

  // const handleApply = () => {
  //   const payload = getPayloadWithChanges(
  //     allFilterSettings,
  //     activeFilterId ?? '',
  //     currentLevelState,
  //   );
  //   dispatch(updateAutoFilterSettings(payload)).then((resp) => {
  //     if (resp.payload) {
  //       dispatch(setAutoFilterLevelFieldSettings(payload));
  //       setAllFilterSettings(payload);
  //       setSettingsModified(false);
  //     }
  //   });
  // };

  const handleApply = () => {
    if (!activeFilterId) return;

    const payload: AutoFilterSettings[] = [
      {
        filterViewexternalId: activeFilterId,
        values: [
          {
            filterText: (minLOIMap[activeFilterId] ?? 2).toString(),
            filterType: 'MINIMUM_LOI',
            uiOrder: 0,
            fieldName: '',
          },
          ...currentLevelState,
        ],
      },
    ];

    dispatch(updateAutoFilterSettings(payload)).then((resp) => {
      if (resp.payload) {
        dispatch(setAutoFilterLevelFieldSettings(payload));
        setAllFilterSettings(payload);
        setSettingsModified(false);
      }
    });
  };

  return (
    <div className='auto-filter-container'>
      <div className='auto-filter-content-holder'>
        {/* <span className='header-text'>LOI Filter Levels</span> */}
        {/* <Tabs selectedIndex={tabIndex} onSelect={(i) => setTabIndex(i)}>
          <TabList>
            {filterViewData.map((item, index) => (
              <Tab key={index}>{item.name}</Tab>
            ))}
          </TabList>
          {filterViewData.map((item) => (
            <TabPanel key={item.externalId}></TabPanel>
          ))}
        </Tabs> */}

        <div className='af-body'>
          <div className='af-body-left'>
            <div className='sub-heading'>Filter Levels</div>
            <div className='field-level-list'>
              <img src={levelFlow} className='fll-img' />
              <ul className='field-level-list-ul'>
                {levels.map((level, index) => {
                  const match = currentLevelState.find((e) => e.uiOrder === index + 1);
                  return (
                    <li key={level}>
                      {level}&nbsp;&nbsp;
                      {match ? (
                        <div className='fll-selected'>
                          {match.fieldName}
                          <img
                            src={removeCross}
                            onClick={() => removeLevel(level, match.filterText)}
                            alt='X'
                          />
                        </div>
                      ) : (
                        <div className='add-new-level'>
                          {index !== 0 ? (
                            <span>Add new level</span>
                          ) : (
                            <span className='fll-default-selected'>{filterViewData[1]?.name}</span>
                          )}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
            {/* <div className='action-sub-content'>
              <button className='action-button' onClick={() => handleSyncLevelData()}>
                Sync
              </button>
              <div className='action-text'>Copy Levels 2-8 to Other LOI Views</div>
            </div> */}
          </div>
          <div className='af-body-right'>
            {/* <div className='afb-min-loi'>
              <div className='action-text'>Minimum LOIs to Enable Auto Filtering</div>
              <div className='gs-input'>
                <input
                  type='number'
                  min={1}
                  max={50}
                  value={minLOIsForAutoFilter}
                  readOnly
                  className='gs-input-small'
                />
                <div className='gs-controls'>
                  <button onClick={increase}>
                    <img src={arrowUp} alt='+' />
                  </button>
                  <button onClick={decrease}>
                    <img src={minLOIsForAutoFilter === 1 ? arrowDownGray : arrowDown} alt='-' />
                  </button>
                </div>
              </div>
            </div> */}
            <div className='af-body-right-header'>Available Fields to Add to Filter Levels</div>
            <div className='field-holder-contents'>
              <div className='field-holder'>
                {availableFields
                  .filter((field) => field.name !== '')
                  .map((field) => (
                    <div
                      className={classNames('field-text', {
                        'level-field-selected': field.code === fieldToBeLevelled[0]?.code,
                      })}
                      onClick={() => setFieldToBeLevelled([field])}
                      key={field.id}>
                      {field.name}
                    </div>
                  ))}
              </div>
              {(fieldToBeLevelled.length > 0 && currentLevelState.length < 7) && (
                <div className='action-footer'>
                  <button className='action-button' onClick={addNewLevel}>
                    Add
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className='bottom-container'>
        <div className='gs-reset-holder'>
          <button className='settings-button-reset' onClick={handleResetToDefault}>
            Reset
          </button>
          <div className='settings-reset-title'>LOI Filter Levels</div>
        </div>
        <div className='gs-button-container'>
          {isSettingsModified ? (
            <>
              <button
                disabled={!isSettingsModified}
                className={classNames('settings-button', {
                  'button-oranges': isSettingsModified,
                })}
                onClick={handleApply}>
                Apply
              </button>
              <button className='settings-button' onClick={handleCancel}>
                Cancel
              </button>
            </>
          ) : (
            <button className='settings-button' onClick={handleClose}>
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
